import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { UseSearchResult, SearchResult, Artist, Artwork, Exhibition } from '../types';
import { useArtists } from './useArtists';
import { useArtworks } from './useArtworks';
import { useExhibitions } from './useExhibitions';

export const useSearch = (): UseSearchResult => {
  const [query, setQuery] = useState<string>('');
  const [debouncedQuery, setDebouncedQuery] = useState<string>('');

  const {
    data,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['search', debouncedQuery],
    queryFn: () => apiService.search(debouncedQuery),
    enabled: debouncedQuery.length >= 2,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: false, // Don't retry failed searches
  });

  // Use mock search as primary since API has database issues
  const shouldUseMockSearch = debouncedQuery.length >= 2;
  
  const { artists } = useArtists({ 
    limit: shouldUseMockSearch ? 200 : 10 
  });
  const artworksQuery = useArtworks({ 
    limit: shouldUseMockSearch ? 200 : 10 
  });
  const artworks = artworksQuery.data?.data || [];
  const { exhibitions } = useExhibitions({ 
    limit: shouldUseMockSearch ? 50 : 10 
  });

  // Mock search functionality as primary search
  const mockSearchResults = useMemo(() => {
    if (!shouldUseMockSearch || !debouncedQuery || debouncedQuery.length < 2) return [];

    const results: SearchResult[] = [];
    const searchLower = debouncedQuery.toLowerCase();

    // Search artists
    artists.forEach((artist: Artist) => {
      const nameMatch = artist.name?.toLowerCase().includes(searchLower);
      const statementMatch = artist.statement?.toLowerCase().includes(searchLower);
      const biographyMatch = artist.biography?.toLowerCase().includes(searchLower);

      if (nameMatch || statementMatch || biographyMatch) {
        const searchResult = {
          type: 'artist' as const,
          id: artist.id,
          title: artist.name || 'ללא שם',
          description: artist.statement || artist.biography || '',
          image_url: artist.primary_artwork?.image_url || artist.image,
          artist_name: artist.name,
          artist_slug: artist.slug || artist.id, // Use ID as fallback if slug is empty
        };
        results.push(searchResult);
      }
    });

    // Search artworks
    artworks.forEach((artwork: Artwork) => {
      const titleMatch = artwork.title?.toLowerCase().includes(searchLower);
      const descriptionMatch = artwork.description?.toLowerCase().includes(searchLower);
      const mediumMatch = artwork.medium?.toLowerCase().includes(searchLower);

      if (titleMatch || descriptionMatch || mediumMatch) {
        const artist = artists.find(a => a.id === artwork.artist_id);
        results.push({
          type: 'artwork',
          id: artwork.id,
          title: artwork.title || 'ללא כותרת',
          description: artwork.description || `${artwork.medium || ''} ${artwork.year_created || ''}`.trim(),
          image_url: artwork.image_url,
          artist_name: artist?.name,
          artist_slug: artist?.slug || artist?.id, // Use ID as fallback if slug is empty
        });
      }
    });

    // Search exhibitions
    exhibitions.forEach((exhibition: Exhibition) => {
      const nameMatch = exhibition.name?.toLowerCase().includes(searchLower);
      const descriptionMatch = exhibition.description?.toLowerCase().includes(searchLower);

      if (nameMatch || descriptionMatch) {
        results.push({
          type: 'exhibition',
          id: exhibition.id,
          title: exhibition.name || 'ללא שם',
          description: exhibition.description || '',
          image_url: exhibition.image_url,
          exhibition_name: exhibition.name,
          exhibition_slug: exhibition.slug,
        });
      }
    });

    return results;
  }, [shouldUseMockSearch, debouncedQuery, artists, artworks, exhibitions]);

  // Debounce search query
  const search = useCallback((searchQuery: string) => {
    setQuery(searchQuery);
    
    const timeoutId = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, []);

  // Use mock search as primary since API has database issues
  const results = mockSearchResults;
  const isSearching = isLoading && mockSearchResults.length === 0;
  const searchError = null; // Hide API errors since we're using mock search

  return {
    search,
    results,
    isSearching,
    error: searchError,
  };
};